// ═══════════════════════════════════════════════════════════
// Correspondence API
//
// Email delivery: GAB notification rule fires on_create of
// correspondence table, sending to the toAddresses field value.
// Notification rule ID: b8085d05-3a84-40a8-947a-f7a0887c6c0b
// (created once via scripts/setup_gab_notifications.py)
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';
import { createAuditEntry } from './auditLog';

export type CorrDirection = 'inbound' | 'outbound' | 'internal';

export interface CorrespondenceRecord {
  recordId:    number;
  requestId:   number;
  direction:   CorrDirection;
  fromUserId?: string;
  toAddresses: string[];   // parsed from JSON field
  subject:     string;
  body:        string;
  templateId?: number;
  isAutomated: boolean;
  attachments: string[];   // |||DOCID||| markers
  sentAt?:     string;
  readAt?:     string;
}

export interface CorrTemplate {
  recordId:      number;
  jurisdictionId:string;
  name:          string;
  trigger:       string;
  subject:       string;
  body:          string;
  isActive:      boolean;
}

const FC = FIELDS.correspondence;
const FT = FIELDS.corrTemplates;

function parseJsonField<T>(val: unknown, fallback: T): T {
  if (!val) return fallback;
  try { return JSON.parse(String(val)) as T; }
  catch { return fallback; }
}

function rowToCorr(row: Record<string, unknown>): CorrespondenceRecord {
  return {
    recordId:    row['record_id'] as number,
    requestId:   Number(row[FC.requestId] ?? 0),
    direction:   (row[FC.direction] as CorrDirection) ?? 'outbound',
    fromUserId:  row[FC.fromUserId] as string | undefined,
    toAddresses: parseJsonField<string[]>(row[FC.toAddresses], []),
    subject:     String(row[FC.subject] ?? ''),
    body:        String(row[FC.body] ?? ''),
    templateId:  row[FC.templateId] as number | undefined,
    isAutomated: row[FC.isAutomated] === true || row[FC.isAutomated] === 'true',
    attachments: parseJsonField<string[]>(row[FC.attachments], []),
    sentAt:      row[FC.sentAt] as string | undefined,
    readAt:      row[FC.readAt] as string | undefined,
  };
}

function rowToTemplate(row: Record<string, unknown>): CorrTemplate {
  return {
    recordId:       row['record_id'] as number,
    jurisdictionId: String(row[FT.jurisdictionId] ?? ''),
    name:           String(row[FT.name] ?? ''),
    trigger:        String(row[FT.trigger] ?? 'manual'),
    subject:        String(row[FT.subject] ?? ''),
    body:           String(row[FT.body] ?? ''),
    isActive:       row[FT.isActive] === true || row[FT.isActive] === 'true',
  };
}

// ── Queries ──────────────────────────────────────────────

export async function listCorrespondenceForRequest(
  requestRecordId: number,
  opts?: { anonymous?: boolean },
): Promise<CorrespondenceRecord[]> {
  const { records } = await queryRecords(TABLES.correspondence, {
    filters: [{ field: FC.requestId, operator: 'eq', value: requestRecordId }],
    sort:    { field: FC.sentAt, direction: 'desc' },
    limit:   200,
  }, opts);
  return (records as Record<string, unknown>[]).map(rowToCorr);
}

export async function listTemplates(jurisdictionId?: string): Promise<CorrTemplate[]> {
  const { records } = await queryRecords(TABLES.corrTemplates, {
    filters: jurisdictionId
      ? [{ field: FT.jurisdictionId, operator: 'eq', value: jurisdictionId }]
      : [],
    limit: 100,
  });
  return (records as Record<string, unknown>[])
    .map(rowToTemplate)
    .filter(t => t.isActive);
}

// ── Send ─────────────────────────────────────────────────

export interface SendCorrespondenceInput {
  requestId:    number;
  direction:    CorrDirection;
  toAddresses?: string[];
  fromUserId?:  string;
  subject:      string;
  body:         string;
  templateId?:  number;
  isAutomated?: boolean;
  attachments?: string[];
  anonymous?:   boolean;
}

/**
 * Creates a correspondence record.
 * For outbound direction with toAddresses, GAB's notification rule
 * (fires on_create) automatically sends the email.
 */
export async function sendCorrespondence(
  input: SendCorrespondenceInput,
  actorEmail?: string,
): Promise<CorrespondenceRecord> {
  const now = new Date().toISOString();
  const row = await createRecord(TABLES.correspondence, {
    [FC.requestId]:   input.requestId,
    [FC.direction]:   input.direction,
    [FC.fromUserId]:  input.fromUserId ?? '',
    [FC.toAddresses]: JSON.stringify(input.toAddresses ?? []),
    [FC.subject]:     input.subject,
    [FC.body]:        input.body,
    [FC.templateId]:  input.templateId ?? null,
    [FC.isAutomated]: input.isAutomated ?? false,
    [FC.attachments]: JSON.stringify(input.attachments ?? []),
    [FC.sentAt]:      now,
  }, { anonymous: input.anonymous });

  if (input.direction !== 'internal') {
    await createAuditEntry({
      requestId:  input.requestId,
      userEmail:  actorEmail ?? '',
      action:     input.direction === 'outbound' ? 'correspondence.sent' : 'correspondence.received',
      details:    JSON.stringify({ subject: input.subject, to: input.toAddresses }),
      anonymous:  input.anonymous,
    });
  }

  return rowToCorr(row as Record<string, unknown>);
}

// ── Merge field substitution ─────────────────────────────

interface MergeContext {
  requestNumber?: string;
  subject?:       string;
  requesterName?: string;
  requesterEmail?:string;
  deadline?:      string;
  daysRemaining?: number;
  department?:    string;
  jurisdiction?:  string;
  assignee?:      string;
}

export function applyMergeFields(template: string, ctx: MergeContext): string {
  return template
    .replace(/\{\{request\.number\}\}/g,   ctx.requestNumber   ?? '')
    .replace(/\{\{request\.subject\}\}/g,  ctx.subject          ?? '')
    .replace(/\{\{requester\.name\}\}/g,   ctx.requesterName    ?? 'Requester')
    .replace(/\{\{requester\.email\}\}/g,  ctx.requesterEmail   ?? '')
    .replace(/\{\{deadline\}\}/g,          ctx.deadline         ?? '')
    .replace(/\{\{days_remaining\}\}/g,    String(ctx.daysRemaining ?? ''))
    .replace(/\{\{department\}\}/g,        ctx.department       ?? '')
    .replace(/\{\{jurisdiction\}\}/g,      ctx.jurisdiction     ?? 'the jurisdiction')
    .replace(/\{\{assignee\}\}/g,          ctx.assignee         ?? '');
}

// ── Seed templates ────────────────────────────────────────

export const BUILT_IN_TEMPLATES: Omit<CorrTemplate, 'recordId'>[] = [
  {
    jurisdictionId: '1',
    name:    'Acknowledgment Letter',
    trigger: 'onSubmission',
    subject: 'Acknowledgment of Public Records Request {{request.number}}',
    body: `Dear {{requester.name}},

Thank you for submitting your public records request to {{jurisdiction}} (Request No. {{request.number}}).

We have received your request dated today regarding: {{request.subject}}

Pursuant to the Freedom of Information Act (Public Records Act § 3 et seq.), we are required to respond to your request within 5 business days of receipt. We will notify you if an extension of time is required.

If you have any questions, please reference your request number {{request.number}} in all correspondence.

Sincerely,
{{jurisdiction}} FOIA Officer`,
    isActive: true,
  },
  {
    jurisdictionId: '1',
    name:    'Extension Notice',
    trigger: 'onExtension',
    subject: 'Extension of Time — Records Request {{request.number}}',
    body: `Dear {{requester.name}},

This letter is to inform you that we require additional time to respond to your public records request (Request No. {{request.number}}) regarding: {{request.subject}}

Pursuant to Public Records Act § 3(e), we are extending the response period. Your new response deadline is {{deadline}}.

The reason for the extension is that the records you requested are stored in locations not immediately accessible to our office and require additional time to retrieve.

If you have any questions, please contact us and reference your request number.

Sincerely,
{{jurisdiction}} FOIA Officer`,
    isActive: true,
  },
  {
    jurisdictionId: '1',
    name:    'Clarification Request',
    trigger: 'manual',
    subject: 'Clarification Needed — Records Request {{request.number}}',
    body: `Dear {{requester.name}},

We are writing regarding your public records request (Request No. {{request.number}}).

To process your request efficiently, we need clarification on the following:

[DESCRIBE CLARIFICATION NEEDED]

Please respond to this email within 5 business days. Please note that the statutory response period is tolled while we await your response.

Thank you for your cooperation.

Sincerely,
{{jurisdiction}} FOIA Officer`,
    isActive: true,
  },
  {
    jurisdictionId: '1',
    name:    'Denial Letter',
    trigger: 'onDenial',
    subject: 'Response to Public Records Request {{request.number}} — Denial',
    body: `Dear {{requester.name}},

This letter is in response to your public records request (Request No. {{request.number}}) dated {{deadline}} regarding: {{request.subject}}

After careful review, we must deny your request in whole/in part pursuant to the following exemption(s) under the Freedom of Information Act (Public Records Act):

[LIST APPLICABLE EXEMPTIONS AND CITATIONS]

[DESCRIBE WITHHELD INFORMATION]

You have the right to have this denial reviewed by the Public Records Ombudsman. A request for review must be made in writing within 60 days after the date of this denial.

Sincerely,
{{jurisdiction}} FOIA Officer`,
    isActive: true,
  },
  {
    jurisdictionId: '1',
    name:    'Records Ready for Release',
    trigger: 'onRelease',
    subject: 'Records Available — Request {{request.number}}',
    body: `Dear {{requester.name}},

Your public records request (Request No. {{request.number}}) has been fulfilled. The responsive records are now available for download through the public portal at the link below.

Request: {{request.subject}}

[INSERT PORTAL LINK OR ATTACHMENT DESCRIPTION]

Some records may have been redacted pursuant to applicable exemptions under the Freedom of Information Act. A description of any withheld information is included with the released records.

Please download your records within 30 days, after which the download link will expire.

Sincerely,
{{jurisdiction}} FOIA Officer`,
    isActive: true,
  },
  {
    jurisdictionId: '1',
    name:    'Fee Estimate',
    trigger: 'onInvoice',
    subject: 'Fee Estimate — Records Request {{request.number}}',
    body: `Dear {{requester.name}},

This letter provides a fee estimate for your public records request (Request No. {{request.number}}) regarding: {{request.subject}}

Pursuant to Public Records Act § 6, fees may be charged for the actual cost of reproducing records and/or staff time required for search and retrieval that exceeds 8 hours.

ESTIMATED FEES:
[FEE BREAKDOWN FROM INVOICE]

Please respond within 5 business days to:
  (a) Authorize payment of the estimated fees
  (b) Narrow the scope of your request to reduce fees, or
  (c) Withdraw your request

Note: The response period is tolled pending your response to this fee estimate.

Sincerely,
{{jurisdiction}} FOIA Officer`,
    isActive: true,
  },
];

export async function seedTemplates(jurisdictionId: string): Promise<void> {
  const existing = await listTemplates(jurisdictionId);
  const existingNames = new Set(existing.map(t => t.name));
  for (const tmpl of BUILT_IN_TEMPLATES) {
    if (existingNames.has(tmpl.name)) continue;
    await createRecord(TABLES.corrTemplates, {
      [FT.jurisdictionId]: jurisdictionId,
      [FT.name]:           tmpl.name,
      [FT.trigger]:        tmpl.trigger,
      [FT.subject]:        tmpl.subject,
      [FT.body]:           tmpl.body,
      [FT.isActive]:       true,
    });
  }
}
